import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const [, navigate] = useLocation();

  const [mode, setMode] = useState<"magic" | "password">("magic");

  // Magic link
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  // Password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Forgot
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login: authLogin } = useAuth();
  const sendMagicLink = trpc.clientAuth.sendMagicLink.useMutation();
  const loginMutation = trpc.clientAuth.login.useMutation();
  const forgotMutation = trpc.clientAuth.forgotPassword.useMutation();

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await sendMagicLink.mutateAsync({ email: magicEmail, origin: window.location.origin });
      setMagicSent(true);
    } catch (err: any) {
      setError(err.message ?? "Erro ao enviar link. Tente novamente.");
    } finally { setLoading(false); }
  }

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const result = await loginMutation.mutateAsync({ email, password });
      const mustChange = !!(result as any).mustChangePassword;
      authLogin({
        token: result.token,
        userId: result.userId,
        name: result.name ?? "",
        email: result.email,
        role: (result.role ?? "viewer") as "admin" | "editor" | "viewer",
        clientId: result.clientId ?? null,
        isRazAdmin: result.isRazAdmin ?? false,
        mustChangePassword: mustChange,
      });
      navigate(mustChange ? "/redefinir-senha" : "/pautas");
    } catch (err: any) {
      setError(err.message ?? "Email ou senha inválidos.");
    } finally { setLoading(false); }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await forgotMutation.mutateAsync({ email: forgotEmail, origin: window.location.origin });
      setForgotSent(true);
    } catch (err: any) {
      setError(err.message ?? "Erro ao enviar email. Tente novamente.");
    } finally { setLoading(false); }
  }

  const inputCls = "w-full bg-white border border-[var(--solar-line)] text-[var(--solar-ink)] rounded-[10px] px-4 py-3 text-sm placeholder-[var(--solar-mute)] focus:outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20 transition";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background relative overflow-hidden">
      {/* Soft brand glow */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-[0.12] pointer-events-none"
           style={{ background: "radial-gradient(circle, #4F46E5 0%, transparent 60%)" }} />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full opacity-[0.35] pointer-events-none"
           style={{ background: "radial-gradient(circle, #E7E3DA 0%, transparent 60%)" }} />

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-[var(--solar-ink)] shadow-[var(--elev-2)]">
            <span className="text-white font-extrabold text-2xl leading-none">s</span>
          </div>
          <div className="text-center">
            <p className="text-[var(--solar-ink)] text-2xl font-extrabold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              socialhub
            </p>
            <p className="text-[var(--solar-mute)] text-[11px] tracking-[0.2em] uppercase font-medium mt-1">
              Social Studio
            </p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-[var(--solar-line)] bg-[var(--solar-card)] shadow-[var(--elev-2)] overflow-hidden">
          {/* Tabs */}
          <div className="flex p-1.5 m-3 mb-0 bg-[#EDEBE6] rounded-full">
            <button
              onClick={() => { setMode("magic"); setError(""); setForgotMode(false); }}
              className={`flex-1 py-2 text-[13px] font-semibold rounded-full transition-all ${mode === "magic" ? "bg-[var(--solar-ink)] text-white" : "text-[var(--solar-mute)] hover:text-[var(--solar-ink)]"}`}
            >
              Link mágico
            </button>
            <button
              onClick={() => { setMode("password"); setError(""); setForgotMode(false); }}
              className={`flex-1 py-2 text-[13px] font-semibold rounded-full transition-all ${mode === "password" ? "bg-[var(--solar-ink)] text-white" : "text-[var(--solar-mute)] hover:text-[var(--solar-ink)]"}`}
            >
              Senha
            </button>
          </div>

          <div className="p-7">
            {/* ── MAGIC LINK ── */}
            {mode === "magic" && (!magicSent ? (
              <>
                <h1 className="text-[var(--solar-ink)] text-xl font-bold mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Entrar sem senha
                </h1>
                <p className="text-[var(--solar-mute)] text-sm mb-6">Informe seu email e enviaremos um link válido por 15 minutos.</p>
                <form onSubmit={handleMagicLink} className="space-y-4">
                  <div>
                    <label className="se-label block mb-1.5 uppercase tracking-wider">Email</label>
                    <input type="email" required value={magicEmail} onChange={e => setMagicEmail(e.target.value)} placeholder="seu@email.com.br" className={inputCls} />
                  </div>
                  {error && <p className="text-[#DC2626] text-sm">{error}</p>}
                  <button type="submit" disabled={loading} className="w-full se-btn !py-3 !rounded-[10px] disabled:opacity-50">
                    {loading ? "Enviando..." : "Enviar link →"}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-2">
                <div className="w-16 h-16 rounded-full bg-[var(--brand-soft)] flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-[var(--solar-ink)] text-lg font-bold mb-2">Link enviado!</h2>
                <p className="text-[var(--solar-mute)] text-sm mb-1">Verifique sua caixa em</p>
                <p className="text-[var(--solar-ink)] text-sm font-semibold mb-4">{magicEmail}</p>
                <p className="text-[var(--solar-mute)] text-xs mb-6">O link expira em 15 minutos. Confira o spam.</p>
                <button onClick={() => { setMagicSent(false); setMagicEmail(""); }} className="text-[var(--solar-mute)] text-sm hover:text-[var(--solar-ink)] transition">
                  ← Usar outro email
                </button>
              </div>
            ))}

            {/* ── PASSWORD ── */}
            {mode === "password" && (!forgotMode ? (
              <>
                <h1 className="text-[var(--solar-ink)] text-xl font-bold mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Entrar com senha
                </h1>
                <p className="text-[var(--solar-mute)] text-sm mb-6">Use as credenciais fornecidas pelo administrador.</p>
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div>
                    <label className="se-label block mb-1.5 uppercase tracking-wider">Email</label>
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="seu@email.com.br" className={inputCls} />
                  </div>
                  <div>
                    <label className="se-label block mb-1.5 uppercase tracking-wider">Senha</label>
                    <div className="relative">
                      <input type={showPassword ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className={`${inputCls} pr-10`} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--solar-mute)] hover:text-[var(--solar-ink)] transition">
                        {showPassword ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  {error && <p className="text-[#DC2626] text-sm">{error}</p>}
                  <button type="submit" disabled={loading} className="w-full se-btn !py-3 !rounded-[10px] disabled:opacity-50">
                    {loading ? "Entrando..." : "Entrar →"}
                  </button>
                  <button type="button" onClick={() => { setForgotMode(true); setError(""); setForgotEmail(email); }} className="w-full text-[var(--solar-mute)] text-xs hover:text-[var(--solar-ink)] transition text-center pt-1">
                    Esqueci minha senha
                  </button>
                </form>
              </>
            ) : (!forgotSent ? (
              <>
                <h1 className="text-[var(--solar-ink)] text-xl font-bold mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Recuperar senha
                </h1>
                <p className="text-[var(--solar-mute)] text-sm mb-6">Enviaremos um link para você criar uma nova senha.</p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="se-label block mb-1.5 uppercase tracking-wider">Email</label>
                    <input type="email" required value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="seu@email.com.br" className={inputCls} />
                  </div>
                  {error && <p className="text-[#DC2626] text-sm">{error}</p>}
                  <button type="submit" disabled={loading} className="w-full se-btn !py-3 !rounded-[10px] disabled:opacity-50">
                    {loading ? "Enviando..." : "Enviar link de recuperação →"}
                  </button>
                  <button type="button" onClick={() => { setForgotMode(false); setError(""); }} className="w-full text-[var(--solar-mute)] text-xs hover:text-[var(--solar-ink)] transition text-center pt-1">
                    ← Voltar ao login
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-2">
                <div className="w-16 h-16 rounded-full bg-[var(--brand-soft)] flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-[var(--solar-ink)] text-lg font-bold mb-2">Email enviado!</h2>
                <p className="text-[var(--solar-mute)] text-sm mb-1">Verifique sua caixa em</p>
                <p className="text-[var(--solar-ink)] text-sm font-semibold mb-4">{forgotEmail}</p>
                <p className="text-[var(--solar-mute)] text-xs mb-6">O link expira em 1 hora. Confira o spam.</p>
                <button onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }} className="text-[var(--solar-mute)] text-sm hover:text-[var(--solar-ink)] transition">
                  ← Voltar ao login
                </button>
              </div>
            )))}
          </div>
        </div>

        <p className="text-center text-[var(--solar-mute)] text-xs mt-6">
          Acesso restrito. Problemas?{" "}
          <a href="mailto:rafael@razconsulting.com.br" className="text-[var(--solar-ink)] hover:underline">
            rafael@razconsulting.com.br
          </a>
        </p>
      </div>
    </div>
  );
}
